/**
 * Design tokens for the shared **empty state** block (`Empty State.dc.html`).
 *
 * One block stands in for every empty list, table and panel in the app. The
 * container's own header and toolbar stay put — only the rows are replaced —
 * so nothing here draws a border or a surface of its own beyond the well.
 *
 * Three sizes, chosen by the *container*, not by the message:
 *   `full`    — a page's main table or a full-width card
 *   `medium`  — a half-width card
 *   `compact` — a widget; no icon, no buttons, one text link
 *
 * Stays screen-agnostic and screen-local to this component until the shared
 * values are promoted into the MUI theme.
 */

import { button, contentCard, formField, notification, signer, white } from "theme/colors";

/** Below this width the block switches to the 375px reference. */
export const EMPTY_PHONE_QUERY = "@media (max-width:599.98px)";

/**
 * Icon accents. These stay orange/violet/green/amber: the standing
 * "orange fills become brand red" rule covers solid *fills* (buttons, badges,
 * selected states) — an icon stroke on a 8%-tint tile is an accent, not a fill.
 */
export const emptyStateAccents = {
  brand: { stroke: signer.orange.main, tint: signer.orange.tint },
  violet: { stroke: signer.purple.main, tint: signer.purple.chipBg },
  green: { stroke: "#1E8A55", tint: notification.success.bg },
  amber: { stroke: signer.warn.main, tint: "#FDF1D8" },
} as const;

export type EmptyStateAccent = keyof typeof emptyStateAccents;

export const emptyStateTokens = {
  /** The recessed panel the block sits in, inside the container's own border. */
  well: {
    bg: formField.bg,
    /**
     * Painted only when the block is `framed` — i.e. when it stands alone on
     * the page instead of replacing the rows of a card that already draws a
     * border. Matches the app's card chrome so the framed well reads as the
     * one box on the screen rather than a second one inside it.
     */
    border: contentCard.border,
    radius: "16px",
    paddingY: { full: "56px", medium: "30px", compact: "22px" },
    paddingX: { full: "24px", medium: "16px", compact: "16px" },
    gap: { full: "13px", medium: "9px", compact: "6px" },
    /** The phone reference drops the well to 36px and narrows the gutter. */
    phonePaddingY: "36px",
    phonePaddingX: "16px",
    phoneGap: "10px",
  },
  tile: {
    size: { full: 46, medium: 38, phone: 42 },
    radius: { full: "13px", medium: "11px", phone: "12px" },
    iconSize: { full: 21, medium: 17, phone: 19 },
    iconStrokeWidth: 1.85,
  },
  title: {
    color: signer.ink,
    fontSize: {
      full: "15.5px",
      medium: "13px",
      compact: "12.5px",
      phone: "14px",
    },
    fontWeight: { full: 800, medium: 800, compact: 700 },
    letterSpacing: "-.01em",
  },
  body: {
    color: signer.text.meta,
    fontSize: { full: "12.5px", medium: "11.5px", phone: "12px" },
    lineHeight: { full: 1.65, medium: 1.55, phone: 1.6 },
    maxWidth: { full: "380px", medium: "240px" },
  },
  actions: {
    gap: "9px",
    marginTop: "4px",
    /** Full size: ghost then primary, side by side. */
    height: 40,
    radius: "9px",
    fontSize: "13px",
    fontWeight: 700,
    primaryBg: button.bg,
    primaryColor: button.color,
    ghostBg: white,
    ghostBorder: formField.border,
    ghostColor: signer.ink,
    /** Medium size collapses to one small ghost button. */
    smallHeight: 34,
    smallRadius: "8px",
    smallPaddingX: "14px",
    smallFontSize: "12px",
    paddingX: { primary: "20px", ghost: "16px" },
    /** The phone reference stacks them full width, primary first, at 44px. */
    phoneHeight: 44,
    phoneGap: "8px",
  },
  /** Compact size: the action is a text link, not a button. */
  link: {
    color: signer.orange.main,
    hoverColor: signer.orange.dark,
    fontSize: "12px",
    fontWeight: 700,
  },
} as const;

export default emptyStateTokens;
